import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Box, Flex } from "@rebass/grid/emotion";
import { Hide } from "@rebass/hide";
import { graphql } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";
import Icon from "react-icons-kit";
import { cameraRetro } from "react-icons-kit/fa/cameraRetro";
import { comments } from "react-icons-kit/fa/comments";
import { folderOpen } from "react-icons-kit/fa/folderOpen";
import { gavel } from "react-icons-kit/fa/gavel";
import { heart } from "react-icons-kit/fa/heart";
import { magic } from "react-icons-kit/fa/magic";
import { phone } from "react-icons-kit/fa/phone";
import { search } from "react-icons-kit/fa/search";
import { trophy } from "react-icons-kit/fa/trophy";
import { users } from "react-icons-kit/fa/users";
import { LinkButton } from "../../components/Button";
import Card from "../../components/Card";
import Contact from "../../components/Contact";
import GoogleReviews from "../../components/GoogleReviews";
import Hero from "../../components/Hero";
import Page from "../../components/Page";
import PageBlock from "../../components/PageBlock";
import PageTitle from "../../components/PageTitle";
import ProfileImage from "../../components/ProfileImage";
import RoundedImage from "../../components/RoundedImage";
import Service from "../../components/Service";
import References from "../../components/References";
import SectionTitle from "../../components/SectionTitle";
import Step from "../../components/Step";
import Testimonials from "../../components/Testimonials";
import ImmoweltBadge from "../../components/ImmoweltBadge";
import SectionTeam from "../../components/SectionTeam";

const WideBackground = styled(Flex)`
  background: radial-gradient(
    90% 150% at 50% 120%,
    rgba(189, 18, 30),
    rgba(105, 31, 131)
  );
  color: white;
  margin-bottom: 100px;
  margin-top: 100px;
`;

const BigIcon = styled(Box)`
  text-align: center;
  color: #961452;
  margin-bottom: 32px;
`;

const ServiceCard = styled(Card)`
  margin-bottom: 32px;
`;

const ImmobilienmaklerErfurt = ({ data }) => (
  <Page
    header={
      <Hero fluid={data.homeHeroImage.fluid}>
        <Flex
          alignItems="center"
          flexWrap="wrap"
          css={css`
            max-width: 50em;
          `}
        >
          <Box
            width={[1, null, 2 / 3]}
            css={css`
              text-align: left;
            `}
          >
            <PageTitle>Immobilienmakler Erfurt</PageTitle>
            <p>
              Der Immobilienmarkt in Erfurt ist stetig in Bewegung. Als{" "}
              <strong>Immobilienmakler für Erfurt</strong> und Umgebung habe ich
              diese Veränderungen stets im Blick. Ganz gleich, ob Sie Eigentümer
              werden wollen oder Ihre Immobilie in Erfurt verkaufen möchten: Mit
              Kranich Immobilien haben Sie einen starken und verlässlichen
              Partner an Ihrer Seite.
            </p>
            <Flex alignItems="stretch" flexWrap="wrap">
              <Hide xsmall>
                <ImmoweltBadge />
              </Hide>
              <GoogleReviews />
            </Flex>
          </Box>
          <Hide width={[1, null, 1 / 3]} paddingLeft={5} xsmall small>
            <ProfileImage version={2} />
            <p>
              <strong>Linda Bäumer</strong>
              <br />
              Makler Erfurt
            </p>
          </Hide>
        </Flex>
      </Hero>
    }
  >
    <Helmet
      title="Immobilienmakler Erfurt"
      meta={[
        {
          name: "description",
          content:
            "Als erfahrener und erfolgreicher Immobilienmakler für Erfurt hilft Ihnen Linda Bäumer beim Kauf und Verkauf von Immobilien.",
        },
      ]}
    />

    <PageBlock>
      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <Card collapsed>
            <Flex alignItems="center" flexWrap="wrap" mx={-3}>
              <Box width={[1, 1, 1 / 3]} px={[3, 3, 3]} mb={[3, 3, 0]}>
                <RoundedImage
                  fluid={data.founderImage.fluid}
                  alt="Linda Bäumer"
                />
              </Box>
              <Box width={[1, null, 2 / 3]} px={3}>
                <h2>Linda Bäumer — Immobilienmakler Erfurt</h2>
                <p>
                  Schon vor über 8 Jahren gründete ich Kranich Immobilien und
                  konnte mir seitdem Stück für Stück den Status einer der besten{" "}
                  <strong>Immobilienmakler in Erfurt</strong> und Umgebung
                  erarbeiten. Heute ist mein Unternehmen nicht nur ein
                  erfolgreiches Maklerbüro, sondern ein stolzes
                  Familienunternehmen.
                </p>
              </Box>
            </Flex>
          </Card>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "30em"]} my={2} p={3}>
          <p style={{ textAlign: "center" }}>
            Was mich in Erfurt so erfolgreich macht? Zielorientiertes Arbeiten
            mit Immobilien aller Art (Haus, Wohnung & Grundstück) gepaart mit
            meinen zwischenmenschlichen Fähigkeiten.{" "}
            <strong>Der Mensch steht bei mir stets an erster Stelle.</strong>
          </p>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <Flex justifyContent="center" flexWrap="wrap" mx={-3}>
            <Box width={[1, null, 1 / 3]} px={3}>
              <BigIcon>
                <Icon size={50} icon={trophy} />
              </BigIcon>
              <h4>8 Jahre voller Erfolg</h4>
              <p>
                Seit der Gründung im Jahr 2012 konnte ich mit meinen Kunden
                unzählige Erfolge feiern. Mit mir an Ihrer Seite sind Sie dank
                meiner Erfahrung jederzeit gut beraten.
              </p>
            </Box>
            <Box width={[1, null, 1 / 3]} px={3}>
              <BigIcon>
                <Icon size={50} icon={comments} />
              </BigIcon>
              <h4>Transparenz schafft Vertrauen</h4>
              <p>
                Transparenz schafft Vertrauen und Vertrauen ist der Schlüssel
                meines Erfolgs. Arbeiten Sie mit mir, so werden Sie stets auf
                dem Laufenden gehalten und meine ehrliche Art zu schätzen
                wissen.
              </p>
            </Box>
            <Box width={[1, null, 1 / 3]} px={3}>
              <BigIcon>
                <Icon size={50} icon={heart} />
              </BigIcon>
              <h4>Der Mensch macht es</h4>
              <p>
                Der Beton mag hart sein, aber die Arbeit mit mir ist von
                Herzlichkeit und Partnerschaftlichkeit geprägt. Sehr gerne bin
                ich das Gegenteil des klischeehaften Maklers.
              </p>
            </Box>
          </Flex>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <SectionTitle center>
            Warum überhaupt einen Makler beauftragen?
          </SectionTitle>
          <Card collapsed mb={4}>
            <Flex alignItems="center" flexWrap="wrap" mx={-3}>
              <Box width={[1, 1, 1 / 3]} px={[3, 3, 3]} mb={[3, 3, 0]}>
                <RoundedImage fluid={data.clockImage.fluid} alt="Uhr" />
              </Box>
              <Box width={[1, null, 2 / 3]} px={3}>
                <h3>
                  Das Wertvollste im Leben: <strong>Zeit</strong>
                </h3>
                <p>
                  Heute kann man sich fast alles kaufen. Doch Zeit ist
                  unkäuflich und für uns alle endlich. Nutzen Sie Ihre Zeit
                  deshalb für die Dinge, die Ihnen wirklich wichtig sind.
                  Während Sie die Dinge tun, die Sie lieben, übernehme ich alle
                  notwendigen Tätigkeiten für Sie. Zum Beispiel:
                </p>
                <ul>
                  <li>Fachgerechte Bewertung Ihrer Immobilie</li>
                  <li>Erstellung von hochprofessionellen Werbemitteln</li>
                  <li>Beschaffung von Objektunterlagen</li>
                  <li>Organisieren und Durchführung von Besichtigungen</li>
                </ul>
              </Box>
            </Flex>
          </Card>
          <Card collapsed>
            <Flex alignItems="center" flexWrap="wrap" mx={-3}>
              <Box width={[1, null, 2 / 3]} px={3}>
                <h3>Mit Sicherheit gut verkauft</h3>
                <p>
                  Eine Immobilie verkauft man nicht jeden Tag. Viele nur einmal
                  im Leben. Neben dem ganzen Gefühlschaos, welches mit einem
                  Verkauf einhergeht, gibt es vieles zu beachten. Meine Aufgabe
                  ist es, Ihnen die nötige Sicherheit zu geben.
                </p>
                <ul>
                  <li>
                    Jeder Kaufinteressent muss vorab detailliert überprüft
                    werden
                  </li>
                  <li>Es gibt kein "über den Tisch ziehen"</li>
                  <li>
                    Ich stehe Ihnen vor, während und natürlich auch nach dem
                    erfolgreichen Verkauf zur Verfügung.
                  </li>
                </ul>
              </Box>
              <Box width={[1, 1, 1 / 3]} px={[3, 3, 3]} mb={[3, 3, 0]}>
                <RoundedImage fluid={data.keyImage.fluid} alt="Schlüssel" />
              </Box>
            </Flex>
          </Card>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <SectionTitle center>Meine Leistungen im Kurzüberblick</SectionTitle>
          <p style={{ textAlign: "center" }}>
            Ganz gleich, ob Haus oder Wohnung: Als Immobilienmakler und Expertin
            für den <strong>Verkauf von Immobilien in Erfurt</strong> wird Ihr
            Objekt schnell, professionell und ertragreich verkauft. Dabei
            übernehme ich sämtliche Aufgaben damit Ihr gewünschtes Ziel
            schnellstmöglich erreicht werden kann. Mit mir als Ihr{" "}
            <strong>Immobilienmakler für Erfurt</strong> und Umgebung erhalten
            Sie ein Rundum-Sorglos-Paket.
          </p>
          <Flex flexWrap="wrap">
            <Service
              icon={search}
              title="Immobilienbewertung"
              description="Bei der Immobilienbewertung schätze ich nicht, sondern ermittle den Wert der Immobilie fach- und sachgerecht."
            />
            <Service
              icon={cameraRetro}
              title="Fotografie und Exposés"
              description="Aussagekräftige Werbemittel sind das A und O. Deshalb werden für Sie ausschließlich hochprofessionelle Werbemittel in bester Qualität erstellt."
            />
            <Service
              icon={magic}
              title="Expertenvermarktung"
              description="Ihre Immobilie wird proaktiv vermarktet. Mit einer regionalen und überregionalen Platzierung des Objektes können wir reichweitenstark potenzielle Käufer ansprechen."
            />
            <Service
              icon={folderOpen}
              title="Bürokratie überwinden"
              description="Die Bürokratie hat die Immobilienwirtschaft fest im Griff. Aber keine Sorge: Es werden alle notwendigen Unterlagen für Sie bereitgestellt und aufgearbeitet."
            />
            <Service
              icon={users}
              title="Besichtigung und Käuferprüfung"
              description="Von der Anfrage bis zur Besichtigung und Bonitätsprüfung. Es wird der perfekte Käufer auf Grundlage Ihrer Kriterien gefunden."
            />
            <Service
              icon={gavel}
              title="Verträge und Notar"
              description="Sobald der passende Käufer gefunden wurde, werden die letzten Schritte bis zur notariellen Beglaubigung sauber vorbereitet.
              "
            />
          </Flex>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} p={3}>
          <ServiceCard red collapsed>
            <h3>Exklusives Homestaging</h3>
            <p>
              Wussten Sie: Weniger als 20 % aller Menschen können sich leere
              Räume möbliert vorstellen und deren Dimensionen richtig
              einschätzen. Als Immobilienmakler weiß ich, dass es für den ersten
              Eindruck keine zweite Chance gibt. Mit dem Homestaging-Service
              verhelfe ich Ihrer Immobilie zum perfekten ersten Eindruck.
            </p>
            <div>
              <LinkButton to="/homestaging/">Mehr zum Homestaging</LinkButton>
            </div>
          </ServiceCard>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <SectionTitle center>Nur 3 Schritte bis zum Verkauf</SectionTitle>
          <Step
            number={1}
            title="Kontakt aufnehmen"
            description="Im ersten Schritt brauchen Sie nur zum Telefon greifen und mich anrufen. In unserem freundlichen Gespräch erzählen Sie mir von Ihrer Immobilie und Ihren Erwartungen."
          />
          <Step
            number={2}
            title="Die Arbeit beginnt"
            description="Ich beginne unverzüglich mit den Vorbereitungen und dem Verkauf Ihrer Immobilie. Von der Wertermittlung bis hin zu den Besichtigungsterminen übernehme ich alle Arbeiten. Natürlich sind Sie dabei stets im Bilde jeglicher Aktivitäten."
          />
          <Step
            number={3}
            title="Wir besiegeln den Verkauf beim Notar"
            description="Im Schnitt benötige ich nur 2 Monate für den Verkauf. Das heißt, wir werden uns sehr schnell beim Notar wiedersehen. Sie verkaufen nun Ihre Immobilie an den perfekten Käufer. Sie haben es geschafft. Herzlichen Glückwunsch."
          />
        </Box>
      </Flex>
    </PageBlock>

    <WideBackground justifyContent="center">
      <Box
        width={[1, null, "50em"]}
        my={4}
        p={3}
        css={css`
          text-align: center;
        `}
      >
        <h1>Machen Sie jetzt den ersten Schritt</h1>
        <p>Nehmen Sie direkt mit Linda Bäumer Kontakt auf:</p>
        <Flex alignItems="center" justifyContent="center" flexWrap="wrap">
          <LinkButton light href="/kontakt">
            Nachricht an Linda schreiben
          </LinkButton>
          <a
            href="tel: 036450 44 99 33"
            css={css`
              color: white;
              padding: 0 24px;
            `}
          >
            <Icon size={32} icon={phone} />
            <span> 036450 / 44 99 33</span>
          </a>
        </Flex>
      </Box>
    </WideBackground>

    <PageBlock>
      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} p={3}>
          <SectionTitle center>Immobilien in Erfurt kaufen</SectionTitle>
          <p>
            Der Immobilienmarkt in Erfurt ist schnelllebig und für Außenstehende
            nur schwer zu greifen. Es gibt momentan zu viele Interessenten.
            Gerade in den Regionen rund um Erfurt, Weimar und Jena spürt man
            dies sehr stark. In diesen Zeiten ist ein vertrauensvoller
            Immobilienmakler goldwert.
          </p>
          <Box mx={[0, 0, -40]}>
            <ServiceCard red collapsed>
              <h3>Aktuelle Angebote</h3>
              <p>
                Sie suchen ein Objekt zum Kauf in Erfurt? Meine Empfehlung:
                Schauen Sie in die Übersicht unserer aktuellen Angebote. Wir
                haben spannende Immobilien aus Erfurt und darüber hinaus.
              </p>
              <div>
                <LinkButton to="/angebote/">Zu den Angeboten</LinkButton>
              </div>
            </ServiceCard>
            <ServiceCard blue collapsed>
              <h3>Finanzierung</h3>
              <p>
                Die Finanzierung ist ein wesentlicher Punkt bei der Erfüllung
                Ihres Traums. Dank meiner Partnerschaft mit Finanzexperten kann
                ich Sie umfangreich beraten und Sie bei der Finanzierung
                unterstützen.
              </p>
              <div>
                <LinkButton to="/finanzierung/">Zur Finanzierung</LinkButton>
              </div>
            </ServiceCard>
            <ServiceCard purple collapsed>
              <h3>VIP-Suche</h3>
              <p>
                Die Suche nach einem passenden Haus oder der perfekten
                Eigentumswohnung kann in Erfurt zum Marathon werden. Nutzen Sie
                meinen VIP-Service, damit ich Ihnen bei Ihrer Suche
                vollumfänglich helfen kann.
              </p>
              <div>
                <LinkButton to="/angebote/">
                  Zur VIP Suche und aktuellen Angeboten
                </LinkButton>
              </div>
            </ServiceCard>
            <ServiceCard red collapsed>
              <h3>Exklusives Homestaging in Erfurt</h3>
              <p>
                Weniger als 20% aller Menschen können sich leere Räume möbliert
                vorstellen und deren Dimensionen richtig abschätzen. Als
                Immobilienmakler weiß ich: Für den ersten Eindruck gibt es keine
                zweite Chance. Mit dem Homestaging-Service verhelfe ich Ihrer
                Immobilie zum perfekten ersten Eindruck.
              </p>
              <div>
                <LinkButton to="/homestaging/">Mehr zum Homestaging</LinkButton>
              </div>
            </ServiceCard>
          </Box>
        </Box>
      </Flex>
    </PageBlock>

    <PageBlock>
        <SectionTeam />
    </PageBlock>

    <PageBlock>
      <Flex justifyContent="center">
        <Box width={[1, null, "70em"]} my={5} p={3} justifyContent="center">
          <SectionTitle center>Was meine Kunden über mich sagen</SectionTitle>
          <Testimonials data={data.testimonials} />
          <Flex
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
          >
            <p>Viele weitere Stimmen zu meiner Arbeit finden Sie auf Google:</p>
            <Box width={[1, "20em"]}>
              <GoogleReviews />
            </Box>
          </Flex>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "60em"]} my={5} p={3} justifyContent="center">
          <SectionTitle center>Referenzobjekte</SectionTitle>
          <Box width={[1, null, "40em"]} margin="auto">
            <p style={{ textAlign: "center" }}>
              Erzählen kann man vieles. Doch ich habe viele Erfolge
              nachzuweisen. Ganz gleich ob Einfamilienhaus, Eigentumswohnung
              oder ungewöhnliche Objekte — aber machen Sie sich doch ein eigenes
              Bild:
            </p>
          </Box>
          <References data={data.references} />
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={2} p={3}>
          <h2>
            Mit Linda Bäumer als Makler in der Region Erfurt jederzeit gut
            beraten
          </h2>
          <p>
            Die meisten Menschen kaufen nur einmal im Leben eine Immobilie.
            Dieser entscheidende Lebensabschnitt kann natürlich Angst machen.
            Auch Eigentümer in Erfurt haben mit vielfältigen Gefühlen zu
            kämpfen. Vor allem bei privaten Verkäufern geht zumeist ein
            Schicksalsschlag voraus. Scheidung, ein unerwarteter Umzug oder der
            Tod eines geliebten Menschen.
          </p>
          <p>Gerne nehme ich Sie an die Hand und biete Ihnen meine Hilfe an.</p>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, 1 / 2]} my={5} p={3}>
          <Card red>
            <ProfileImage />
            <Box mt={3}>
              <h3>Herausforderungen gemeinsam angehen</h3>
              <p>
                Möchten Sie mit mir über Ihre Immobilie sprechen? Füllen Sie
                dafür bitte das Formular aus. Ich melde mich danach so schnell
                wie möglich bei Ihnen. Einfach, schnell und unverbindlich.
              </p>
              <Contact />
            </Box>
          </Card>
        </Box>
      </Flex>
    </PageBlock>
  </Page>
);

export const query = graphql`
  query {
    homeHeroImage: imageSharp(
      fluid: { originalName: { regex: "/home-hero/" } }
    ) {
      fluid(maxWidth: 1600, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    founderImage: imageSharp(fluid: { originalName: { regex: "/linda_2/" } }) {
      fluid(maxWidth: 500, maxHeight: 500, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    clockImage: imageSharp(fluid: { originalName: { regex: "/clock/" } }) {
      fluid(maxWidth: 500, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    keyImage: imageSharp(fluid: { originalName: { regex: "/key/" } }) {
      fluid(maxWidth: 500, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    references: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/src/references/" } }
      sort: { fields: [frontmatter___order] }
      limit: 3
    ) {
      edges {
        node {
          frontmatter {
            image {
              childImageSharp {
                fluid(maxWidth: 500, quality: 80) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
            location
            type
            order
            difficulty
            price
            time
          }
          html
        }
      }
    }
    testimonials: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/src/testimonials/" } }
      sort: { fields: [fileAbsolutePath] }
    ) {
      edges {
        node {
          frontmatter {
            image {
              childImageSharp {
                fluid(maxWidth: 250, quality: 80) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
            author
          }
          html
        }
      }
    }
  }
`;

export default ImmobilienmaklerErfurt;
